/**
 * Cookie-consent storage + application (PDPL / Privacy Policy §10).
 *
 * Necessary cookies always run. Non-essential trackers — Google Analytics
 * (analytics) and any marketing tags — load ONLY after the user consents.
 * index.html defines window.__safaqatLoadGA() but does NOT call it on first
 * visit; it (and this module) call it only once analytics consent is granted.
 */

const CONSENT_KEY = 'safaqat24.cookieConsent';

export interface CookieConsent {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
}

declare global {
  interface Window {
    __safaqatLoadGA?: () => void;
  }
}

export function getConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? (JSON.parse(raw) as CookieConsent) : null;
  } catch {
    return null;
  }
}

export function hasDecided(): boolean {
  return getConsent() !== null;
}

/** Enable the non-essential trackers the user has consented to. Idempotent. */
export function applyConsent(consent: CookieConsent | null): void {
  if (typeof window === 'undefined') return;
  if (consent?.analytics && typeof window.__safaqatLoadGA === 'function') {
    window.__safaqatLoadGA();
  }
}

/** Persist the user's choice and immediately apply it. */
export function setConsent(analytics: boolean, marketing: boolean): CookieConsent {
  const consent: CookieConsent = {
    necessary: true,
    analytics,
    marketing,
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {
    /* storage blocked — the choice applies for this session only */
  }
  applyConsent(consent);
  return consent;
}
